/**
 * Centralized Error Handling Utilities
 * 
 * Provides consistent error logging and user notification across the app.
 * 
 * USAGE PATTERNS:
 * 
 * 1. In hooks returning error state:
 *    - Log the error with logError()
 *    - Set error state, let UI decide whether to show toast
 *    
 * 2. For direct user actions (button clicks, form submissions):
 *    - Use handleError() to both log and show toast
 *    
 * 3. For background operations:
 *    - Use logError() only, unless operation is critical
 */

import { toast } from "@/components/ui/sonner";
import { 
  isStructuredError, 
  getErrorMessage,
  RecoveryAction 
} from "@/types/errors";

// Re-export for convenience - single source of truth in errors.ts
export { getErrorMessage } from "@/types/errors";

/**
 * Logs error to console with context
 * 
 * @param context - Where the error occurred (e.g., 'Checkout', 'Contact Form')
 * @param error - The error object
 * 
 * @example
 * logError('Product fetch', error);
 */
export function logError(context: string, error: unknown): void {
  const message = getErrorMessage(error);
  console.error(`[${context}]`, message, error);
}

/**
 * Shows error notification to user using Sonner toast
 * 
 * @param message - Primary error message
 * @param description - Optional detailed description
 * @param recoveryAction - Optional recovery action guidance
 * 
 * @example
 * showErrorToast('Checkout failed', 'Please try again or contact support', RecoveryAction.RETRY);
 */
export function showErrorToast(
  message: string, 
  description?: string,
  recoveryAction?: RecoveryAction
): void {
  const actionText = recoveryAction ? getRecoveryActionText(recoveryAction) : undefined;
  
  toast.error(message, {
    description: description || actionText,
    duration: 5000,
  });
}

/**
 * Get user-friendly text for recovery action
 */
function getRecoveryActionText(action: RecoveryAction): string {
  switch (action) {
    case RecoveryAction.RETRY:
      return 'Please try again';
    case RecoveryAction.REFRESH:
      return 'Please refresh the page';
    case RecoveryAction.CONTACT_SUPPORT:
      return 'Please contact support if the problem persists';
    case RecoveryAction.GO_BACK:
      return 'Please go back and try again';
    default:
      return '';
  }
}

/**
 * Shows success notification to user using Sonner toast
 * 
 * @param message - Primary success message
 * @param description - Optional detailed description
 */
export function showSuccessToast(message: string, description?: string): void {
  toast.success(message, {
    description,
    duration: 4000,
  });
}

/**
 * Combined error handler: logs to console and shows user notification
 * 
 * Use this for user-initiated actions where feedback is expected.
 * 
 * @param context - Where the error occurred
 * @param error - The error object (can be StructuredError or any error type)
 * @param options - Customization options
 * 
 * @example
 * try {
 *   await createCheckout();
 * } catch (error) {
 *   handleError('Checkout', error, {
 *     userMessage: 'Failed to create checkout',
 *     showToast: true
 *   });
 * }
 */
export function handleError(
  context: string,
  error: unknown,
  options?: {
    userMessage?: string;
    showToast?: boolean;
    description?: string;
  }
): void {
  const { userMessage, showToast = true, description } = options || {};
  
  // Always log to console
  logError(context, error);
  
  // Optionally show user notification
  if (showToast) {
    const message = userMessage || getErrorMessage(error);
    const recoveryAction = isStructuredError(error) ? error.recoveryAction : undefined;
    showErrorToast(message, description, recoveryAction);
  }
}
